import React, { useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { DateTime } from 'luxon';
import { UsageChart, Header, UsageTable, UsageThisMonth } from './particles';
import { callGetBillingAccount, useSrtTokens } from '@api';
import { Card } from '@atoms';

interface BillingAccountDetailsProps extends RouteComponentProps {
  locationId?: string;
  billingAccountId?: string;
  type: 'superadmin' | 'admin';
}

const BillingAccountDetails: React.FC<BillingAccountDetailsProps> = (
  props: BillingAccountDetailsProps
) => {
  const { locationId, billingAccountId } = props;
  const searchParams = new URLSearchParams(props?.location?.search);

  const [start, setStart] = useState<DateTime>(
    searchParams.has('date')
      ? DateTime.fromFormat(searchParams.get('date')!, 'yyyy-LL-dd').startOf('month')
      : DateTime.now().startOf('month')
  );
  const [end, setEnd] = useState<DateTime>(
    searchParams.has('date')
      ? DateTime.fromFormat(searchParams.get('date')!, 'yyyy-LL-dd')
      : DateTime.now()
  );
  const billingAccountQuery = useQuery({
    queryKey: ['company-token-by-id', locationId, billingAccountId],
    queryFn: () => callGetBillingAccount({ locationId, billingAccountId }),
    enabled: !!(locationId || billingAccountId)
  });

  const billingAccount: BillingAccount | undefined =
    billingAccountQuery.data?.code === 200 &&
    billingAccountQuery.data.data?.data &&
    billingAccountQuery.data.data.data.length > 0
      ? billingAccountQuery.data.data.data[0]
      : undefined;
  const { data: responseSrt, isFetching: isLoading } = useSrtTokens<SrtToken[]>({
    from: start.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    }),
    to: end.toISO({
      suppressMilliseconds: true,
      includeOffset: false
    }),
    locationId,
    billingAccountId
  });

  if (!locationId && !billingAccountId) {
    return <div>Id is Missing</div>;
  }

  return (
    <Box>
      <Card sx={{ m: 4, p: 4 }}>
        <Header
          billingAccount={billingAccount}
          dateTime={start}
          onSelectRange={(start, end) => {
            setStart(start);
            setEnd(end);
          }}
        />

        <Box>
          <UsageChart usageData={responseSrt?.data} dates={{ start, end }} loading={isLoading} />
          <UsageThisMonth usageData={responseSrt?.data} company={billingAccount} />
          <UsageTable
            dates={{ start, end }}
            locationId={locationId}
            billingAccountId={billingAccountId}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default BillingAccountDetails;
